import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Hash,
  Package,
  Scale,
} from "lucide-react";
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useSeller } from "../contexts/SellerContext";
import {
  getOrderWeights,
  getOrderSettledAmount,
  getOrderSettledFineWt,
} from "../utils/orderWeights";
import Header from "./Header";
import Footer from "./Footer";
import "./SellerOrderHistory.css";

const parseNum = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatRupee = (amount) =>
  `₹${parseNum(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatWeight = (weight) => `${parseNum(weight).toFixed(3)}g`;

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = value?.seconds
    ? new Date(value.seconds * 1000)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getSettlementTime = (settlement) =>
  settlement.paymentDeliveredToSellerAt ||
  settlement.dispatchedToSellerAt ||
  settlement.settledAt ||
  settlement.createdAt;

const getTimeValue = (value) => {
  if (!value) return 0;
  if (value?.seconds) return value.seconds * 1000;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const isSettledStatus = (status) => {
  const s = (status || "").toString().toLowerCase();
  return (
    s === "settled" ||
    s === "paid to seller" ||
    s === "paymentdeliveredtoseller"
  );
};

const normalizeStatus = (status) => {
  const statusMap = {
    settled: "Settled",
    paymentDispatchedtoSeller: "On the way",
    paymentDispatchedToSeller: "On the way",
    paymentDeliveredtoSeller: "Paid to seller",
    paymentDeliveredToSeller: "Paid to seller",
  };
  return statusMap[status] || status || "Settlement created";
};

const chunk = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

// Qty / gross / net now come from the per-size breakdown (per-set x qty) and
// prefer the operator's `settlementData` snapshot. Gross and net used to be read
// straight off `order.grossWt` / `order.netWt`, which is a SINGLE set's weight,
// so a 5-set order rendered as "Qty 5" beside one set's gross/net.
const getOrderQuantity = (order) => getOrderWeights(order).qty;
const getOrderGrossWt = (order) => getOrderWeights(order).grossWt;
const getOrderNetWt = (order) => getOrderWeights(order).netWt;

// Fine weight: prefer the settled snapshot, else derive from the corrected net wt
const getOrderFineWt = (order) => getOrderSettledFineWt(order);

// Amount: prefer the operator's per-order settled figure. `totalPaymentAmount`
// is deliberately excluded — it carries the GROUP settlement total, so reading
// it per card repeated the whole settlement amount on every product card.
const getOrderAmount = (order) => getOrderSettledAmount(order);

const getPrimaryImage = (order) =>
  order.images?.[0]?.url?.trim() ||
  order.imageUrl?.trim() ||
  order.productImage?.trim() ||
  "";

const SellerOrderHistory = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const [settlements, setSettlements] = useState([]);
  const [ordersById, setOrdersById] = useState({});
  const [operatorNames, setOperatorNames] = useState({});
  const [jreNames, setJreNames] = useState({});
  const [courierDetails, setCourierDetails] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNames = async () => {
      const operatorIds = [
        ...new Set(settlements.map((s) => s.operatorId).filter(Boolean)),
      ];
      // JRE row is only needed for non-settled settlements that actually have a JRE.
      const jreIds = [
        ...new Set(
          settlements
            .filter((s) => !isSettledStatus(s.sellerPaymentStatus) && s.jreId)
            .map((s) => s.jreId)
            .filter(Boolean),
        ),
      ];
      const opNames = {};
      await Promise.all(
        operatorIds.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "operatorregistrations", id));
            if (snap.exists()) {
              const d = snap.data();
              opNames[id] =
                d.organizationName ||
                d.businessName ||
                d.operatorName ||
                d.name ||
                id;
            } else {
              opNames[id] = id;
            }
          } catch {
            opNames[id] = id;
          }
        }),
      );
      const jNames = {};
      await Promise.all(
        jreIds.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "jreregistrations", id));
            if (snap.exists()) {
              const d = snap.data();
              const full =
                d.fullName ||
                [d.firstName, d.lastName].filter(Boolean).join(" ").trim();
              jNames[id] = d.jreName || d.name || full || d.displayName || id;
            } else {
              jNames[id] = id;
            }
          } catch {
            jNames[id] = id;
          }
        }),
      );
      setOperatorNames(opNames);
      setJreNames(jNames);
    };
    if (settlements.length > 0) fetchNames();
  }, [settlements]);

  // Resolve courier branch details for settlements that came via courier.
  // Branch docs live at couriers/{courierId}/branches/{branchId}.
  useEffect(() => {
    const fetchCouriers = async () => {
      const branchIds = [
        ...new Set(
          settlements
            .flatMap((s) =>
              (s.orderIds || []).map((id) => ordersById[id]?.courierId),
            )
            .filter(Boolean),
        ),
      ];
      if (branchIds.length === 0) return;
      const details = {};
      await Promise.all(
        branchIds.map(async (branchId) => {
          try {
            const snap = await getDocs(
              query(
                collectionGroup(db, "branches"),
                where("branchId", "==", branchId),
              ),
            );
            if (!snap.empty) {
              const b = snap.docs[0].data();
              const courierId = snap.docs[0].ref.parent.parent?.id || null;
              let courierName = b.courierName || null;
              if (courierId && !courierName) {
                try {
                  const cSnap = await getDoc(doc(db, "couriers", courierId));
                  if (cSnap.exists()) courierName = cSnap.data().name || null;
                } catch {
                  // keep branch-level values
                }
              }
              details[branchId] = {
                courierName,
                branchName: b.branchName || null,
                branchCode: b.branchCode || null,
                contactPerson: b.contactPerson || null,
                contactNumber:
                  (b.contactNumber || "").toString().trim() || null,
              };
            }
          } catch {
            // leave unresolved -> order-level denormalized fields used instead
          }
        }),
      );
      setCourierDetails(details);
    };
    if (settlements.length > 0 && Object.keys(ordersById).length > 0)
      fetchCouriers();
  }, [settlements, ordersById]);

  useEffect(() => {
    const fetchSellerHistory = async () => {
      if (!sellerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const settlementQuery = query(
          collection(db, "settlements"),
          where("sellerId", "==", sellerId),
        );
        const settlementSnap = await getDocs(settlementQuery);
        const fetchedSettlements = settlementSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort(
            (a, b) =>
              getTimeValue(getSettlementTime(b)) -
              getTimeValue(getSettlementTime(a)),
          );

        setSettlements(fetchedSettlements);

        const orderIds = Array.from(
          new Set(
            fetchedSettlements
              .flatMap((settlement) => settlement.orderIds || [])
              .filter(Boolean),
          ),
        );

        if (orderIds.length === 0) {
          setOrdersById({});
          return;
        }

        const orderEntries = {};
        for (const ids of chunk(orderIds, 10)) {
          const ordersQuery = query(
            collection(db, "orderList"),
            where(documentId(), "inside", ids),
          );
          const ordersSnap = await getDocs(ordersQuery);
          ordersSnap.forEach((docSnap) => {
            orderEntries[docSnap.id] = {
              firestoreId: docSnap.id,
              ...docSnap.data(),
            };
          });
        }

        // 🔍 Debug: log what we found
        console.log("[SellerOrderHistory] settlement orderIds:", orderIds);
        console.log(
          "[SellerOrderHistory] fetched ordersById keys:",
          Object.keys(orderEntries),
        );
        const sampleOrder = Object.values(orderEntries)[0];
        if (sampleOrder) {
          console.log(
            "[SellerOrderHistory] sample order fields:",
            Object.keys(sampleOrder),
          );
          console.log("[SellerOrderHistory] sample order data:", sampleOrder);
        } else {
          console.warn(
            "[SellerOrderHistory] No orders fetched — orderIds may not match orderList doc IDs",
          );
        }

        setOrdersById(orderEntries);
      } catch (err) {
        console.error("[SellerOrderHistory] Failed to load history:", err);
        setError(`Failed to load seller order history: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerHistory();
  }, [sellerId]);

  const summary = useMemo(() => {
    return settlements.reduce(
      (acc, settlement) => {
        // Order count: prefer explicit count, fall back to orderIds length
        acc.orders += parseNum(
          settlement.ordersCount ?? settlement.orderIds?.length,
        );

        // Fine weight: prefer settlement-level field, fall back to summing from fetched orders
        const settlementFine = parseNum(
          settlement.totalFineWeightDue ??
            settlement.totalFine ??
            settlement.sellerSettlement?.fine ??
            settlement.totalFineWeight,
        );
        if (settlementFine > 0) {
          acc.fine += settlementFine;
        } else {
          // Compute from individual fetched orders
          (settlement.orderIds || []).forEach((id) => {
            const order = ordersById[id];
            if (order) acc.fine += getOrderFineWt(order);
          });
        }

        // Amount = the SETTLEMENT amount due to the seller. Deliberately NOT
        // `totalAmountPaid` — that is the cash figure the operator typed into the
        // Pay modal, which differs from the MC owed whenever any part is settled
        // by RTGS / banking, or when the operator left a cash field at zero.
        const settlementAmount = parseNum(
          settlement.totalAmountDue ??
            settlement.sellerSettlement?.amount ??
            settlement.totalAmount ??
            settlement.totalPaymentAmount,
        );
        if (settlementAmount > 0) {
          acc.amount += settlementAmount;
        } else {
          (settlement.orderIds || []).forEach((id) => {
            const order = ordersById[id];
            if (order) acc.amount += getOrderAmount(order);
          });
        }

        return acc;
      },
      { orders: 0, fine: 0, amount: 0 },
    );
  }, [settlements, ordersById]);

  return (
    <>
      <Header title="Order History" />
      <main className="seller-history-page">
        <section
          className="seller-history-summary"
          aria-label="Seller history summary"
        >
          <div>
            <p className="history-eyebrow">Seller settlements</p>
            <h1>Order History</h1>
          </div>
          <div className="history-summary-grid">
            <div className="history-summary-item">
              <Package size={18} />
              <span>{Math.round(summary.orders)}</span>
              <small>Orders</small>
            </div>
            <div className="history-summary-item">
              <Scale size={18} />
              <span>{formatWeight(summary.fine)}</span>
              <small>Fine wt</small>
            </div>
            <div className="history-summary-item">
              <CreditCard size={18} />
              <span>{formatRupee(summary.amount)}</span>
              <small>Amount</small>
            </div>
          </div>
        </section>

        {loading && (
          <div className="history-state">Loading order history...</div>
        )}
        {error && <div className="history-state history-error">{error}</div>}

        {!loading && !error && settlements.length === 0 && (
          <div className="history-state">
            No settlement history found for this seller.
          </div>
        )}

        {!loading && !error && settlements.length > 0 && (
          <section className="history-list" aria-label="Settlement list">
            {settlements.map((settlement) => {
              const settlementId =
                settlement.sellerSettlementId ||
                settlement.settlementId ||
                settlement.id;
              const isExpanded = expandedId === settlement.id;
              const settlementOrders = (settlement.orderIds || [])
                .map(
                  (orderId) => ordersById[orderId] || { firestoreId: orderId },
                )
                .filter(Boolean);
              const totalFine =
                settlement.totalFineWeightDue ||
                settlement.totalFine ||
                settlement.sellerSettlement?.fine;
              // Settlement amount due to the seller. Deliberately NOT
              // `totalAmountPaid` — that is the cash figure the operator typed
              // into the Pay modal, not the MC actually owed.
              const totalAmount =
                settlement.totalAmountDue ||
                settlement.sellerSettlement?.amount ||
                settlement.totalAmount ||
                settlement.totalPaymentAmount;

              return (
                <article className="history-card" key={settlement.id}>
                  <button
                    type="button"
                    className="history-card-header"
                    onClick={() =>
                      setExpandedId(isExpanded ? null : settlement.id)
                    }
                    aria-expanded={isExpanded}
                  >
                    <div className="history-card-title">
                      <span className="history-status">
                        <CheckCircle size={14} />
                        {normalizeStatus(settlement.status)}
                      </span>
                      <strong>{settlementId}</strong>
                      <span className="history-date">
                        <Calendar size={13} />
                        {formatDateTime(getSettlementTime(settlement))}
                      </span>
                    </div>

                    <div className="history-card-totals">
                      <span>
                        {settlement.ordersCount ||
                          settlement.orderIds?.length ||
                          0}{" "}
                        orders
                      </span>
                      <span>{formatWeight(totalFine)}</span>
                      <span>{formatRupee(totalAmount)}</span>
                      {isExpanded ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="history-card-body">
                      <div className="history-meta-grid">
                        <div>
                          <small>Operator</small>
                          <span>
                            {settlement.operatorName ||
                              operatorNames[settlement.operatorId] ||
                              settlement.operatorId ||
                              "N/A"}
                          </span>
                        </div>
                        {!isSettledStatus(settlement.sellerPaymentStatus) &&
                        settlement.jreId ? (
                          <div>
                            <small>JRE</small>
                            <span>
                              {settlement.jreName ||
                                jreNames[settlement.jreId] ||
                                settlement.jreId}
                            </span>
                          </div>
                        ) : null}
                        {(() => {
                          const courierOrder = settlementOrders.find(
                            (o) => o.courierId,
                          );
                          const branchId = courierOrder?.courierId;
                          const resolved =
                            (branchId && courierDetails[branchId]) || {};
                          const cName =
                            courierOrder?.courierName ||
                            resolved.courierName ||
                            null;
                          const bName =
                            courierOrder?.courierBranchName ||
                            resolved.branchName ||
                            null;
                          const cPerson =
                            courierOrder?.courierContactPerson ||
                            resolved.contactPerson ||
                            null;
                          const cNumber =
                            courierOrder?.courierContactNumber ||
                            resolved.contactNumber ||
                            null;
                          if (!branchId && !cName && !bName && !cNumber)
                            return null;
                          return (
                            <div>
                              <small>Courier</small>
                              <span>
                                {cName && bName
                                  ? `${cName} — ${bName}`
                                  : bName || cName || "Courier assigned"}
                                {(cPerson || cNumber) && (
                                  <>
                                    <br />
                                    {cPerson ? `${cPerson}: ` : ""}
                                    {cNumber || ""}
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })()}
                        <div>
                          <small>Payment status</small>
                          <span>
                            {settlement.sellerPaymentStatus || "pending"}
                          </span>
                        </div>
                        <div>
                          <small>Verified</small>
                          <span>
                            {settlement.sellerPaymentVerified ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>

                      <div className="history-orders-heading">
                        Orders in this settlement
                      </div>
                      <div className="history-orders">
                        {settlementOrders.length === 0 ? (
                          <div className="history-order-empty">
                            No order ids saved for this settlement.
                          </div>
                        ) : (
                          settlementOrders.map((order) => {
                            const imageUrl = getPrimaryImage(order);
                            return (
                              <div
                                className="history-order-row"
                                key={order.firestoreId}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={order.productName || "Product"}
                                  />
                                ) : (
                                  <div className="history-image-placeholder">
                                    No Image
                                  </div>
                                )}
                                <div className="history-order-info">
                                  <strong>
                                    {order.productName ||
                                      "Order details pending"}
                                  </strong>
                                  <span>
                                    <Hash size={12} />
                                    {order.firestoreId}
                                  </span>
                                  <div className="history-order-metrics">
                                    <span>Qty {getOrderQuantity(order)}</span>
                                    <span>
                                      Gross{" "}
                                      {formatWeight(getOrderGrossWt(order))}
                                    </span>
                                    <span>
                                      Net {formatWeight(getOrderNetWt(order))}
                                    </span>
                                    <span>
                                      Fine {formatWeight(getOrderFineWt(order))}
                                    </span>
                                    <span>
                                      Amount{" "}
                                      {formatRupee(getOrderAmount(order))}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};

export default SellerOrderHistory;
